import { Scene, EntityBuilder, UIEntityBuilder, globalEventBus, globalTheme } from 'agent-gamedev';
import type { IWorld, SceneTransitionData } from 'agent-gamedev';
import { GAME_WIDTH, GAME_HEIGHT, PALETTE } from '../constants';
import { PeerConnection } from '../network/PeerConnection';
import { LockstepEngine } from '../network/LockstepEngine';
import { multiplayerState } from '../network/MultiplayerState';
import { SignalingClient } from '../network/SignalingClient';
import type { NetMessage } from '../network/types';

const W = GAME_WIDTH;
const H = GAME_HEIGHT;
const DEFAULT_SIGNALING_URLS = [
  'wss://signal.qlplay.top',
  'wss://brickpush-signal.wolf173331.workers.dev',
];

export class LobbyScene extends Scene {
  readonly name = 'LobbyScene';
  private overlayEl: HTMLDivElement | null = null;
  private touchHandlers: Array<{ evt: string; fn: () => void }> = [];

  onEnter(world: IWorld, _data?: SceneTransitionData): void {
    globalTheme.setTheme('retro');
    multiplayerState.reset();

    this.trackEntity(EntityBuilder.create(world, W, H).withBackground({ color: PALETTE.MENU_BG }).build());

    this.trackEntity(UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: -180, width: 600, height: 80 })
      .withText({ text: '联机对战', fontSize: 52, color: PALETTE.TITLE_YELLOW, align: 'center' })
      .build());

    this.trackEntity(UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: -100, width: 700, height: 40 })
      .withText({ text: '好友联机建议创建房间；随机玩一局可用快速匹配', fontSize: 16, color: PALETTE.SUBTITLE_WHITE, align: 'center' })
      .build());

    this.trackEntity(UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: -20, width: 260, height: 54 })
      .withButton({ label: '快速匹配', onClick: 'lobby:match', borderRadius: 8 })
      .build());

    this.trackEntity(UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: 48, width: 260, height: 54 })
      .withButton({ label: '创建房间', onClick: 'lobby:create-room', borderRadius: 8 })
      .build());

    this.trackEntity(UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: 116, width: 260, height: 54 })
      .withButton({ label: '输入房间号', onClick: 'lobby:join-room', borderRadius: 8 })
      .build());

    this.trackEntity(UIEntityBuilder.create(world, W, H)
      .withUITransform({ anchor: 'center', y: 194, width: 260, height: 50 })
      .withButton({ label: '返回菜单', onClick: 'scene:menu', borderRadius: 8 })
      .build());

    const matchHandler = () => this.startMatchmaking(world);
    const createRoomHandler = () => this.startMatchmaking(world, 'room-host');
    const joinRoomHandler = () => this.showJoinRoomOverlay(world);
    globalEventBus.on('lobby:match', matchHandler);
    globalEventBus.on('lobby:create-room', createRoomHandler);
    globalEventBus.on('lobby:join-room', joinRoomHandler);
    this.touchHandlers.push({ evt: 'lobby:match', fn: matchHandler });
    this.touchHandlers.push({ evt: 'lobby:create-room', fn: createRoomHandler });
    this.touchHandlers.push({ evt: 'lobby:join-room', fn: joinRoomHandler });
  }

  // ------------------------------------------------------------------
  // 开始匹配：通过 WebSocket 信令服务器自动配对
  // ------------------------------------------------------------------
  private startMatchmaking(world: IWorld, mode: 'match' | 'room-host' | 'room-join' = 'match', roomId = ''): void {
    this.closeOverlay();
    const wrap = this.buildOverlay();

    const title = document.createElement('div');
    title.textContent = mode === 'match' ? '正在匹配...' : mode === 'room-host' ? '创建房间' : '加入房间';
    title.style.cssText = 'color:#ffe7a3;font-size:24px;font-weight:700;text-align:center;';

    const status = document.createElement('div');
    status.textContent = '正在连接服务器...';
    status.style.cssText = 'color:#88cc88;font-size:16px;text-align:center;min-height:22px;';

    const roomCode = document.createElement('div');
    roomCode.style.cssText = 'display:none;color:#ffffff;font-size:36px;font-weight:800;letter-spacing:8px;text-align:center;';

    const hint = document.createElement('div');
    hint.style.cssText = 'color:#aeb8d6;font-size:13px;text-align:center;line-height:1.45;min-height:18px;';
    hint.textContent = this.getSignalingHint();

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = '取消';
    cancelBtn.style.cssText = 'padding:8px 18px;border:1px solid #8086a2;border-radius:6px;background:#1f2337;color:#e9edf8;font-weight:700;cursor:pointer;';

    const copyBtn = document.createElement('button');
    copyBtn.textContent = '复制房间号';
    copyBtn.style.cssText = 'display:none;padding:8px 18px;border:1px solid #e0b84f;border-radius:6px;background:#2c2412;color:#ffe7a3;font-weight:700;cursor:pointer;';

    wrap.appendChild(title);
    wrap.appendChild(status);
    wrap.appendChild(roomCode);
    wrap.appendChild(hint);
    wrap.appendChild(copyBtn);
    wrap.appendChild(cancelBtn);
    this.mountOverlay(wrap);

    const peer = new PeerConnection();
    multiplayerState.peer = peer;

    let connected = false;

    const signalingUrls = this.getSignalingUrls();
    let signaling: SignalingClient | null = null;
    let canceled = false;
    let currentSignalIndex = 0;
    let signalingConnectTimer = 0;

    const sendMatchRequest = () => {
      if (connected) return;
      window.clearTimeout(signalingConnectTimer);
      if (mode === 'match') {
        status.textContent = '正在寻找对手...';
        signaling?.sendMatch();
      } else if (mode === 'room-host') {
        status.textContent = '正在创建房间...';
        signaling?.sendCreateRoom();
      } else {
        status.textContent = `正在加入房间 ${roomId.toUpperCase()}...`;
        signaling?.sendJoinRoom(roomId);
      }
    };

    const handleRoomCreated = (createdRoomId: string) => {
      if (connected) return;
      status.textContent = '把房间号发给好友，等待加入...';
      status.style.color = '#88cc88';
      roomCode.textContent = createdRoomId;
      roomCode.style.display = 'block';
      copyBtn.style.display = 'inline-block';
      copyBtn.onclick = () => {
        navigator.clipboard?.writeText(createdRoomId).then(() => {
          copyBtn.textContent = '已复制';
        }).catch(() => {
          copyBtn.textContent = '复制失败';
        });
      };
    };

    const handleMatched = (role: 'host' | 'joiner', matchedRoomId?: string) => {
      if (connected) return;
      status.textContent = matchedRoomId
        ? `房间 ${matchedRoomId} 已会合，正在建立连接...`
        : '找到对手，正在建立连接...';
      status.style.color = '#88cc88';

      if (role === 'host') {
        multiplayerState.localPlayerId = 0;
        peer.createOffer().then((offer) => {
          if (connected) return;
          signaling?.sendOffer(offer);
        }).catch((e) => {
          status.textContent = '创建失败: ' + String(e);
          status.style.color = '#ff7777';
        });
      } else {
        multiplayerState.localPlayerId = 1;
        // joiner 等待 host 的 offer
      }
    };

    const handleOffer = (sdp: string) => {
      if (connected) return;
      // 只有 joiner 会收到 offer
      peer.acceptOffer(sdp).then((answer) => {
        if (connected) return;
        signaling?.sendAnswer(answer);
      }).catch((e) => {
        status.textContent = '连接失败: ' + String(e);
        status.style.color = '#ff7777';
      });
    };

    const handleAnswer = (sdp: string) => {
      if (connected) return;
      // 只有 host 会收到 answer
      peer.acceptAnswer(sdp).catch((e) => {
        status.textContent = '连接失败: ' + String(e);
        status.style.color = '#ff7777';
      });
    };

    const handlePeerDisconnected = () => {
      if (!connected) {
        status.textContent = '对手已断开';
        status.style.color = '#ff7777';
      }
      multiplayerState.cleanup();
    };

    const connectSignaling = () => {
      if (connected || canceled) return;
      const url = signalingUrls[currentSignalIndex];
      window.clearTimeout(signalingConnectTimer);
      signaling?.close();
      signaling = new SignalingClient(url);
      status.textContent = currentSignalIndex === 0
        ? '正在连接服务器...'
        : `正在尝试备用线路 ${currentSignalIndex + 1}/${signalingUrls.length}...`;
      status.style.color = '#88cc88';
      if (mode === 'room-host') {
        roomCode.style.display = 'none';
        copyBtn.style.display = 'none';
        copyBtn.textContent = '复制房间号';
      }

      const tryNextSignaling = (fallbackStatus: string, finalStatus: string) => {
        if (connected || canceled) return;
        const hasFallback = currentSignalIndex < signalingUrls.length - 1;
        if (hasFallback) {
          currentSignalIndex += 1;
          status.textContent = fallbackStatus;
          status.style.color = '#ffcc66';
          signaling?.close();
          window.setTimeout(connectSignaling, 350);
          return;
        }

        status.textContent = finalStatus;
        status.style.color = '#ff7777';
        signaling?.close();
      };

      signaling.onConnected(sendMatchRequest);
      signaling.onRoomCreated(handleRoomCreated);
      signaling.onMatched(handleMatched);
      signaling.onOffer(handleOffer);
      signaling.onAnswer(handleAnswer);
      signaling.onPeerDisconnected(handlePeerDisconnected);
      signaling.onError((msg) => {
        if (connected || canceled) return;
        status.textContent = `信令错误: ${msg}`;
        status.style.color = '#ffcc66';
      });
      signaling.onClose((event, manual) => {
        window.clearTimeout(signalingConnectTimer);
        if (connected || canceled || manual) return;
        const reason = event.code ? `(${event.code}${event.reason ? ` ${event.reason}` : ''})` : '';
        tryNextSignaling('信令断开，正在切换线路...', `连接已关闭 ${reason}`.trim());
      });
      signalingConnectTimer = window.setTimeout(() => {
        tryNextSignaling('连接超时，正在切换线路...', '连接服务器超时，请稍后重试');
      }, 6500);
      signaling.connect();
    };

    peer.onOpen(() => {
      if (connected) return;
      connected = true;
      window.clearTimeout(timeoutId);
      window.clearTimeout(signalingConnectTimer);
      signaling?.close();
      this.onPeerConnected(world, peer);
    });

    peer.onClose(() => {
      if (!connected) {
        status.textContent = '连接已断开';
        status.style.color = '#ff7777';
      }
      multiplayerState.cleanup();
    });

    // 房间模式允许玩家把房间号发给好友，等待时间更长。
    const timeoutId = window.setTimeout(() => {
      if (!connected) {
        status.textContent = mode === 'room-host' ? '房间等待超时，请重新创建' : '匹配超时，请稍后重试';
        status.style.color = '#ff7777';
        window.clearTimeout(signalingConnectTimer);
        signaling?.close();
        multiplayerState.cleanup();
      }
    }, mode === 'room-host' ? 120000 : 20000);

    cancelBtn.onclick = () => {
      canceled = true;
      window.clearTimeout(timeoutId);
      window.clearTimeout(signalingConnectTimer);
      signaling?.sendCancel();
      signaling?.close();
      this.closeOverlay();
      multiplayerState.cleanup();
    };

    connectSignaling();
  }

  private showJoinRoomOverlay(world: IWorld): void {
    this.closeOverlay();
    const wrap = this.buildOverlay();

    const title = document.createElement('div');
    title.textContent = '输入房间号';
    title.style.cssText = 'color:#ffe7a3;font-size:24px;font-weight:700;text-align:center;';

    const input = document.createElement('input');
    input.maxLength = 4;
    input.placeholder = 'ABCD';
    input.autocomplete = 'off';
    input.inputMode = 'text';
    input.style.cssText = [
      'height:52px', 'border:2px solid #e0b84f', 'border-radius:8px',
      'background:#0a0a1a', 'color:#ffffff', 'font-size:28px',
      'font-weight:800', 'text-align:center', 'letter-spacing:8px',
      'text-transform:uppercase', 'outline:none',
    ].join(';');
    input.addEventListener('input', () => {
      input.value = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    });

    const status = document.createElement('div');
    status.textContent = '让好友先创建房间，然后输入 4 位房间号。';
    status.style.cssText = 'color:#aeb8d6;font-size:14px;text-align:center;min-height:22px;';

    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:10px;justify-content:center;';

    const joinBtn = document.createElement('button');
    joinBtn.textContent = '加入';
    joinBtn.style.cssText = 'padding:9px 22px;border:1px solid #e0b84f;border-radius:6px;background:#2c2412;color:#ffe7a3;font-weight:800;cursor:pointer;';

    const cancelBtn = document.createElement('button');
    cancelBtn.textContent = '取消';
    cancelBtn.style.cssText = 'padding:9px 22px;border:1px solid #8086a2;border-radius:6px;background:#1f2337;color:#e9edf8;font-weight:700;cursor:pointer;';

    const submit = () => {
      const roomId = input.value.trim().toUpperCase();
      if (!/^[A-Z0-9]{4}$/.test(roomId)) {
        status.textContent = '请输入 4 位房间号';
        status.style.color = '#ff9999';
        return;
      }
      this.startMatchmaking(world, 'room-join', roomId);
    };

    joinBtn.onclick = submit;
    cancelBtn.onclick = () => this.closeOverlay();
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') submit();
    });

    actions.appendChild(joinBtn);
    actions.appendChild(cancelBtn);
    wrap.appendChild(title);
    wrap.appendChild(input);
    wrap.appendChild(status);
    wrap.appendChild(actions);
    this.mountOverlay(wrap);
    input.focus();
  }

  private getSignalingHint(): string {
    const configured = this.getSignalingUrls().some((url) => url.startsWith('wss://'));
    if (configured) return '正在使用已配置的信令服务。';
    return '未配置生产信令地址时，仅本地开发可用。上线前请设置 VITE_SIGNALING_SERVER_URL。';
  }

  private getSignalingUrls(): string[] {
    const configured = (import.meta.env.VITE_SIGNALING_SERVER_URL as string | undefined) || '';
    const urls = configured
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean);

    if (import.meta.env.DEV) {
      urls.push('ws://localhost:8080');
    }
    urls.push(...DEFAULT_SIGNALING_URLS);

    return Array.from(new Set(urls));
  }

  private onPeerConnected(_world: IWorld, peer: PeerConnection): void {
    multiplayerState.connected = true;
    multiplayerState.isMultiplayer = true;
    multiplayerState.gameSeed = Math.floor(Math.random() * 1000000);

    const lockstep = new LockstepEngine(multiplayerState.localPlayerId);
    multiplayerState.lockstep = lockstep;

    const handshake = JSON.stringify({ type: 'handshake', playerId: multiplayerState.localPlayerId, seed: multiplayerState.gameSeed });
    peer.send(handshake);

    peer.onMessage((raw) => {
      try {
        const msg = JSON.parse(raw) as NetMessage;
        if (msg.type === 'handshake') {
          multiplayerState.gameSeed = multiplayerState.gameSeed ^ msg.seed;
        }
      } catch {
        // ignore
      }
    });

    setTimeout(() => {
      if (!this.isActive) return;
      lockstep.start();
      globalEventBus.emit('scene:game-multiplayer');
    }, 300);
  }

  private buildOverlay(): HTMLDivElement {
    const panel = document.createElement('div');
    panel.style.cssText = [
      'display:flex', 'flex-direction:column', 'gap:12px',
      'width:520px', 'max-width:calc(100% - 32px)',
      'padding:18px 20px', 'border:2px solid #e0b84f',
      'border-radius:14px', 'background:rgba(10, 10, 26, 0.96)',
      'box-shadow:0 12px 32px rgba(0,0,0,0.45)',
    ].join(';');
    return panel;
  }

  private mountOverlay(panel: HTMLDivElement): void {
    if (typeof document === 'undefined') return;
    const container = document.getElementById('game-container');
    if (!container) return;

    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:absolute', 'inset:0', 'display:flex',
      'align-items:center', 'justify-content:center',
      'background:rgba(4, 4, 12, 0.72)', 'z-index:40',
      'pointer-events:auto',
    ].join(';');

    overlay.appendChild(panel);
    container.appendChild(overlay);
    this.overlayEl = overlay;
  }

  private closeOverlay(): void {
    if (this.overlayEl) {
      this.overlayEl.remove();
      this.overlayEl = null;
    }
  }

  onExit(_world: IWorld): void {
    this.closeOverlay();
    for (const h of this.touchHandlers) {
      globalEventBus.off(h.evt, h.fn);
    }
    this.touchHandlers = [];
  }
}
